/**
 * Provides user authentication on the `EventBus`.
 *
 * @module EventBusUser
 * @author Jacob Viertel <jv@onscreen.net>
 *
 * @import { UserData } from '../../model';
 *
 * @see [EventBus](../../core/context/event-bus.js)
 * @todo import authUser
 */

import {GlobalWatchGroup, createGlobalContext, globalValue, provide, value} from '@acng/frontend-relativity';
import {pick} from '@acng/frontend-bounty/object.js';

import {user} from '../../user/user.js';
import {discardToken, useToken} from '../service/token.js';
import {ctxAuthUser, provideAuthUser} from './auth-user.js';
import {ctxEventBus, listen, publish, request} from 'acng/core/context/event-bus.js';
import {CONST, INTEGER, NULLABLE, OBJECT, OPTIONAL, STRING, guard} from '@acng/frontend-rubicon';
import {EVENTBUS_USER} from '../../model';

const MODULE = 'userPool/context/event-bus-user';
const VERBOSE = false;
DEBUG: if (VERBOSE) console.warn(`import verbose ${MODULE}`);

/**
 * Observe the authenticated even bus user.
 *
 * @type {import('@acng/frontend-relativity').LegacyContext<UserData | null> & import('@acng/frontend-relativity').ObservableValue<UserData | null>}
 */
export const ctxEventBusUser = createGlobalContext(null);

export const getEventBusUser = () => globalValue(ctxEventBusUser);

export {listen, publish, request};

(() => {
  const TROI_AUTH = 'auth';

  /**
   * Send the user auth to `TROI`.
   *
   * @param {UserData | null} authUser
   */
  const login = async (authUser) => {
    if (!authUser) {
      return;
    }

    const loginData = {
      type: TROI_AUTH,
      user: pick(authUser, 'id', 'pool_id'),
      token: await useToken(),
    };

    DEBUG: if (VERBOSE) console.info(`${MODULE} <---`, loginData);

    publish(loginData);
  };

  listen(TROI_AUTH, (data) => {
    DEBUG: if (VERBOSE) console.debug(`${MODULE} --->`, data);
    ASSERT: guard(data, EVENTBUS_AUTH);

    const authUser = value(ctxAuthUser);

    // TROI responded with an invalid WORF token.
    if (data.token) {
      discardToken(data.token);
      login(authUser);
      return;
    }

    if (authUser && authUser.id == data.user?.id) {
      DEBUG: if (VERBOSE) console.info(`${MODULE} provide`, {authUser});

      provide(ctxEventBusUser, authUser);
    }
  });

  listen('user', (data) => {
    ASSERT: guard(data, EVENTBUS_USER);
    // const $rootScope = inject('$rootScope');

    // $rootScope.$applyAsync(function () {
      const flush = user.fsk != data.user.fsk || user.level != data.user.level;

      user.coins = data.user.coins;
      user.level = data.user.level;
      user.fsk = data.user.fsk;

      if (flush) {
        provideAuthUser(user);
      }
    // });
  });

  GlobalWatchGroup(
    ctxEventBus,
    ctxAuthUser
  )(([isConnected, authUser], [wasConnected, prevUser]) => {
    DEBUG: if (VERBOSE) console.debug(`${MODULE} watch`, {isConnected, wasConnected, authUser, prevUser});

    if (!isConnected) {
      if (authUser && wasConnected) {
        DEBUG: console.warn(`${MODULE} disconnect`);

        provide(ctxEventBusUser, null);
      }
      return;
    }

    if (!wasConnected) {
      login(authUser);
      return;
    }

    if (authUser && authUser.id == prevUser?.id) {
      DEBUG: console.info(`same authUser`, {authUser});
      return;
    }

    if (prevUser) {
      const logoutData = {
        type: TROI_AUTH,
        user: null,
      };

      DEBUG: if (VERBOSE) console.info(`${MODULE} <---`, logoutData);

      provide(ctxEventBusUser, null);
      publish(logoutData);
    }

    login(authUser);
  });
})();

const EVENTBUS_AUTH = /* @__PURE__ */ (() =>
  OBJECT({
    type: CONST('auth'),
    user: NULLABLE(
      OBJECT({
        pool_id: INTEGER,
        id: INTEGER,
      })
    ),
    token: OPTIONAL(STRING),
  }))();
